import { assign, forEach, includes } from 'lodash';
import turf from '@turf/turf';

import * as annotationOps from '../../api/Annotations/annotation-operations';
import { LABEL_TYPES, ELEMENT_TYPES } from '../../api/AnnotationTemplates/AnnotationTemplates';
import * as templateOps from '../../api/AnnotationTemplates/annotation-template-operations';

import { usToDDHHMMSS } from '../dates';
import { featurePoint2KML, featureLinestring2KML } from '../geojson-utilities';

/**
 * Generates a string of XML from the video annotations and LineStrings.
 *
 * @param {object} arg - The arguments object.
 * @param {object[]} arg.annotations - The video annotations.
 * @param {object[]} arg.lines - The video LineStrings.
 * @returns {string} The annotations and LineStrings in kml format.
 */
const generateAnnotationsKML = ({ annotations, lines }) => {
  let kml =
    '<?xml version="1.0" encoding="UTF-8"?><kml xmlns="http://www.opengis.net/kml/2.2" xmlns:gx="http://www.google.com/kml/ext/2.2" xmlns:kml="http://www.opengis.net/kml/2.2" xmlns:atom="http://www.w3.org/2005/Atom"><Document>';

  forEach(annotations, annotation => {
    const coordinates = turf.getCoord(annotationOps.getGeoJSON(annotation));

    const properties = {
      x: coordinates[0],
      y: coordinates[1],
      videoTime: usToDDHHMMSS(annotationOps.getVideoTime(annotation)),
      name: templateOps.getName(annotationOps.getTemplate(annotation))
    };

    let labelIndex = 0;
    let imageIndex = 0;
    let videoIndex = 0;
    let iframeIndex = 0;
    let linkIndex = 0;

    forEach(templateOps.getElements(annotationOps.getTemplate(annotation)), element => {
      switch (true) {
        case includes(LABEL_TYPES, templateOps.getElementType(element)):
          assign(properties, {
            [`Text ${labelIndex} (${templateOps.getElementLabelName(
              element
            )})`]: templateOps.getElementValue(element)
          });
          labelIndex += 1;
          break;

        case ELEMENT_TYPES.IMAGE === templateOps.getElementType(element):
          assign(properties, {
            [`Image ${imageIndex}`]: `<img width="100%" src="${templateOps.getElementValue(
              element
            )}" />`
          });
          imageIndex += 1;
          break;

        case ELEMENT_TYPES.VIDEO === templateOps.getElementType(element):
          assign(properties, {
            [`Video ${videoIndex}`]: `<video src="${templateOps.getElementValue(element)}" />`
          });
          videoIndex += 1;
          break;

        case ELEMENT_TYPES.IFRAME === templateOps.getElementType(element):
          assign(properties, {
            [`Iframe ${iframeIndex}`]: `<iframe src="${templateOps.getElementValue(element)}" />`
          });
          iframeIndex += 1;
          break;

        case includes(
          [ELEMENT_TYPES.LINK, ELEMENT_TYPES.ICON],
          templateOps.getElementType(element)
        ):
          assign(properties, {
            [`Link ${linkIndex}`]: `<a href="${templateOps.getElementValue(element)}" />`
          });
          linkIndex += 1;
          break;

        default:
          break;
      }
    });

    const annotationAsGeoJSON = turf.point(coordinates, properties);
    kml += featurePoint2KML(annotationAsGeoJSON);
  });

  forEach(lines, line => {
    kml += featureLinestring2KML(line);
  });
  kml += '</Document></kml>';

  return kml;
};

export default generateAnnotationsKML;
